<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted } from 'vue'
import { RouterLink, RouterView } from 'vue-router'
import {
  Activity,
  BookOpen,
  CandlestickChart,
  Clock3,
  Database,
  Layers3,
  LayoutDashboard,
  LineChart,
  Orbit,
  RefreshCw,
  ServerOff,
  Settings,
  ShieldCheck,
  Target,
  TriangleAlert,
  Wifi
} from 'lucide-vue-next'
import { useMarketStore } from './stores/market'
import { compactNumber, shortTime } from './utils/format'

const store = useMarketStore()

const quote = computed(() => store.currentQuote)
const basis = computed(() => store.currentBasis)
const instrumentName = computed(() => store.profile?.display_name ?? '股指期货')
const connectionText = computed(() => {
  if (store.connection === 'live') return store.isSample ? '样例通道' : '实时通道'
  if (store.connection === 'reconnecting') return '正在重连'
  if (store.connection === 'offline') return '接口离线'
  return '正在连接'
})

const nav = [
  { to: '/dashboard', index: '01', label: '驾驶舱', icon: LayoutDashboard },
  { to: '/evidence/basis', index: '02', label: '基差与期限', icon: LineChart },
  { to: '/evidence/positions', index: '03', label: '持仓结构', icon: Layers3 },
  { to: '/evidence/options', index: '04', label: '期权波动', icon: Orbit },
  { to: '/review', index: '05', label: 'AI研判', icon: BookOpen },
  { to: '/settings', index: '06', label: '系统配置', icon: Settings }
]

onMounted(() => {
  void store.initialize()
})

onBeforeUnmount(() => {
  store.dispose()
})
</script>

<template>
  <div class="app-shell">
    <header class="topbar">
      <div class="brand">
        <div class="brand-mark">
          <CandlestickChart :size="19" :stroke-width="1.9" />
        </div>
        <div>
          <div class="brand-name">穿山甲股指</div>
          <div class="brand-sub">{{ instrumentName }}期货工作台</div>
        </div>
      </div>

      <div class="top-tools">
        <div :class="['status-pill', `connection-${store.connection}`]">
          <Wifi :size="13" :stroke-width="2" />
          {{ connectionText }}
        </div>
        <div v-if="store.isSample" class="status-pill sample-pill">
          <TriangleAlert :size="13" />
          非实时数据
        </div>
        <span class="top-time"><Clock3 :size="12" />{{ shortTime(quote?.source_time) }}</span>
      </div>
    </header>

    <div class="workspace">
      <aside class="sidebar">
        <div class="nav-label">WORKSPACE</div>
        <RouterLink
          v-for="item in nav"
          :key="item.to"
          :to="item.to"
          class="nav-link"
          active-class="active"
        >
          <span class="nav-icon"><component :is="item.icon" :size="17" :stroke-width="1.8" /></span>
          <span class="nav-copy">{{ item.label }}</span>
          <span class="nav-index">{{ item.index }}</span>
        </RouterLink>

        <div class="sidebar-spacer" />

        <div class="focus-contract">
          <div class="focus-label"><Target :size="13" />FOCUS CONTRACT</div>
          <div class="focus-symbol">
            {{ store.contract }}
            <span v-if="quote?.contract_id === store.contract">观察中</span>
          </div>
          <div class="focus-meta">
            到期 {{ store.dashboard?.focus_contract.expiry_date ?? '—' }}<br />
            OI {{ store.dashboard?.focus_contract ? compactNumber(quote?.open_interest, 0) : '—' }} 手<br />
            视角：日内
          </div>
          <div class="focus-presence">
            <ShieldCheck :size="13" />
            条件与数据状态已启用
          </div>
        </div>
      </aside>

      <main class="main-area">
        <div v-if="store.loading" class="state-screen">
          <div class="state-icon"><Activity :size="32" /></div>
          <h2>正在加载中...</h2>
          <p>加载合约、现货、基差与盘中证据。最长等待 12 秒。</p>
        </div>

        <div v-else-if="store.error && !store.dashboard" class="state-screen error-state">
          <div class="state-icon"><ServerOff :size="32" /></div>
          <h2>数据服务暂时不可用</h2>
          <p>{{ store.error }}</p>
          <button class="primary-button" @click="store.retry">
            <RefreshCw :size="15" />
            重新连接
          </button>
        </div>

        <RouterView v-else />
      </main>
    </div>

    <footer class="statusbar">
      <div class="status-left">
        <span class="freshness"><Database :size="12" />stockAPI {{ store.connection === 'live' ? '已连接' : '连接异常' }}</span>
        <span class="divider" />
        <span>{{ store.dashboard?.trade_date ?? '—' }}</span>
        <span>{{ store.dashboard?.session ?? '—' }}</span>
        <span v-if="basis">基差 {{ compactNumber(basis.basis_points) }} 点</span>
      </div>
      <div class="status-right">
        <span>{{ store.isSample ? '样例数据 · 不用于实时决策' : '实时数据' }}</span>
        <span class="divider" />
        <span class="footer-time"><Clock3 :size="12" />北京时间 {{ shortTime(quote?.received_at) }}</span>
      </div>
    </footer>
  </div>
</template>
